import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/sessions';

const C = {
  bg:'#0D1B2A', surface:'#112233', surface2:'#162840',
  border:'rgba(255,255,255,.08)', border2:'rgba(255,255,255,.12)',
  text:'#F0F6FF', text2:'#A8BFD0', text3:'#607080',
  gold:'#C9A84C', amber:'#F59E0B', amberBg:'rgba(245,158,11,.12)',
  accent:'#3AAF58', accentBg:'rgba(58,175,88,.12)',
  red:'#EF4444', redBg:'rgba(239,68,68,.12)',
  blue:'#3B82F6', blueBg:'rgba(59,130,246,.12)',
  orange:'#F97316', orangeBg:'rgba(249,115,22,.12)',
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const URGENCY = {
  High:   {color:'#EF4444', bg:'rgba(239,68,68,.15)', icon:'🔴'},
  Medium: {color:'#F59E0B', bg:'rgba(245,158,11,.15)', icon:'🟠'},
  Low:    {color:'#10B981', bg:'rgba(16,185,129,.15)', icon:'🟢'},
};

const MENU_CLASS = {
  Star:       {color:'#F59E0B', bg:'rgba(245,158,11,.15)', icon:'⭐', desc:'Popular + Profitable'},
  Plowhorse:  {color:'#3B82F6', bg:'rgba(59,130,246,.15)', icon:'🐎', desc:'Popular + Low Margin'},
  Puzzle:     {color:'#8B5CF6', bg:'rgba(139,92,246,.15)', icon:'🧩', desc:'Profitable + Low Popularity'},
  Dog:        {color:'#EF4444', bg:'rgba(239,68,68,.15)', icon:'🐕', desc:'Low Popularity + Low Margin'},
};

export default function KitchenAI() {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [analyzing, setAnalyzing] = useState(false);
  const [demandForecast, setDemandForecast] = useState(null);
  const [menuAnalysis, setMenuAnalysis] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/ai/kitchen-context`);
      const d = await r.json();
      setContext(d);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { loadContext(); }, [loadContext]);

  const runAnalysis = async (type) => {
    if (!context) return;
    setAnalyzing(true);
    try {
      const r = await fetch(`${API}/ai/kitchen-analysis`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ context, type })
      });
      const d = await r.json();
      if (r.ok) {
        if (type==='demand') { setDemandForecast(d); setActiveTab('demand'); }
        else if (type==='menu_engineering') { setMenuAnalysis(d); setActiveTab('menu'); }
        else { setBriefing(d); setActiveTab('briefing'); }
      }
    } catch(e) {}
    setAnalyzing(false);
  };

  const askKitchen = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(p=>[...p,{role:'user',content:msg}]);
    setChatLoading(true);
    try {
      const r = await fetch(`${API}/ai/chat`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          system: `You are Botanix Kitchen AI — expert in F&B operations, menu planning, food cost management, and kitchen management for a premium nature resort. Current data: ${JSON.stringify({stats:context?.stats, low_stock:context?.low_stock, upcoming:context?.upcoming_bookings})}`,
          messages: [...chatMessages.slice(-6), {role:'user',content:msg}],
        })
      });
      const d = await r.json();
      setChatMessages(p=>[...p,{role:'assistant',content:d.content?.[0]?.text||'No response'}]);
    } catch(e) { setChatMessages(p=>[...p,{role:'assistant',content:'Error'}]); }
    setChatLoading(false);
  };

  if (loading) return <div style={{background:C.bg,minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:C.text3}}>Loading Kitchen AI...</div>;

  const stats = context?.stats || {};

  return (
    <div style={{background:C.bg,minHeight:'100vh',color:C.text,padding:'24px 28px',fontFamily:'system-ui,sans-serif'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.text}}>🍽️ Kitchen Intelligence AI</div>
          <div style={{fontSize:13,color:C.text3,marginTop:2}}>Demand forecasting · Menu engineering · Food cost · Purchase intelligence</div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button onClick={loadContext} style={{padding:'8px 12px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:12,cursor:'pointer'}}>↻</button>
          <button onClick={()=>runAnalysis('briefing')} disabled={analyzing}
            style={{padding:'9px 14px',background:C.accentBg,color:C.accent,border:`1px solid ${C.accent}44`,borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',opacity:analyzing?.6:1}}>
            📋 Daily Brief
          </button>
          <button onClick={()=>runAnalysis('demand')} disabled={analyzing}
            style={{padding:'9px 14px',background:C.amberBg,color:C.amber,border:`1px solid ${C.amber}44`,borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',opacity:analyzing?.6:1}}>
            📊 Demand Forecast
          </button>
          <button onClick={()=>runAnalysis('menu_engineering')} disabled={analyzing}
            style={{padding:'9px 16px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',opacity:analyzing?.6:1}}>
            {analyzing?'⏳':'🧠'} Menu Engineering
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20,flexWrap:'wrap'}}>
        {[
          ['overview','🏠 Overview'],
          ['briefing','📋 Daily Brief'],
          ['demand','📊 Demand Forecast'],
          ['stock','📦 Inventory Alerts'],
          ['menu','⭐ Menu Engineering'],
          ['chat','💬 Kitchen AI Chat'],
        ].map(([id,label])=>(
          <button key={id} onClick={()=>setActiveTab(id)}
            style={{padding:'7px 14px',background:activeTab===id?C.gold:'transparent',color:activeTab===id?'#1A1A1A':C.text2,
              border:`1px solid ${activeTab===id?C.gold:C.border}`,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
            {label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab==='overview' && (
        <div>
          {/* KPIs */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
            {[
              {label:'Menu Items',value:stats.total_menu_items||0,icon:'🍽️',color:C.gold},
              {label:'Low Stock Items',value:stats.low_stock_count||0,icon:'📦',color:stats.low_stock_count>0?C.red:C.accent},
              {label:'Upcoming Events',value:stats.upcoming_events||0,icon:'📅',color:C.blue},
              {label:'Covers Today',value:stats.max_covers_today||0,icon:'👥',color:C.amber},
            ].map((k,i)=>(
              <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 18px',borderTop:`3px solid ${k.color}`}}>
                <div style={{fontSize:24,marginBottom:8}}>{k.icon}</div>
                <div style={{fontSize:28,fontWeight:900,color:k.color}}>{k.value}</div>
                <div style={{fontSize:11,color:C.text3,marginTop:4,textTransform:'uppercase'}}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* Upcoming bookings + meal covers */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.amber,marginBottom:14}}>📅 Upcoming Events (7 days)</div>
              {(context?.upcoming_bookings||[]).length===0?(
                <div style={{textAlign:'center',padding:20,color:C.text3,fontSize:13}}>No upcoming events</div>
              ):(context?.upcoming_bookings||[]).map((b,i)=>(
                <div key={i} style={{padding:'10px 14px',background:C.surface2,borderRadius:8,marginBottom:8}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{b.guest_name||'Group'}</div>
                    <div style={{fontSize:13,fontWeight:700,color:C.amber}}>{b.pax_count} pax</div>
                  </div>
                  <div style={{fontSize:11,color:C.text3}}>{b.booking_type?.replace(/[^\w\s]/g,'').trim()} · {new Date(b.start_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</div>
                </div>
              ))}
            </div>

            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:14}}>🍽️ Meal Covers Forecast</div>
              {Object.keys(context?.meal_covers||{}).length===0?(
                <div style={{textAlign:'center',padding:20,color:C.text3,fontSize:13}}>No upcoming covers</div>
              ):Object.entries(context?.meal_covers||{}).slice(0,5).map(([date,covers])=>(
                <div key={date} style={{padding:'10px 14px',background:C.surface2,borderRadius:8,marginBottom:8}}>
                  <div style={{fontSize:12,color:C.text3,marginBottom:6}}>{new Date(date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6}}>
                    {[['🌅 Breakfast',covers.breakfast],['☀️ Lunch',covers.lunch],['🌙 Dinner',covers.dinner]].map(([label,count])=>(
                      <div key={label} style={{textAlign:'center',padding:'6px',background:C.bg,borderRadius:6}}>
                        <div style={{fontSize:16,fontWeight:800,color:C.amber}}>{count}</div>
                        <div style={{fontSize:9,color:C.text3}}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Low stock alert */}
          {(context?.low_stock||[]).length > 0 && (
            <div style={{background:C.surface,border:`1px solid ${C.red}44`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.red,marginBottom:14}}>⚠️ Low Stock Alert ({context.low_stock.length} items)</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:8}}>
                {context.low_stock.slice(0,8).map((item,i)=>(
                  <div key={i} style={{padding:'10px 12px',background:C.surface2,borderRadius:8,border:`1px solid ${item.shortage_pct>50?C.red:C.amber}33`}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.text}}>{item.item_name}</div>
                    <div style={{display:'flex',justifyContent:'space-between',marginTop:4}}>
                      <span style={{fontSize:11,color:C.text3}}>{item.current_stock} {item.unit} left</span>
                      <span style={{fontSize:11,fontWeight:700,color:item.shortage_pct>50?C.red:C.amber}}>{item.shortage_pct}% short</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* DAILY BRIEF */}
      {activeTab==='briefing' && (
        <div>
          {!briefing ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>📋</div>
              <div style={{fontSize:14,color:C.text2,marginBottom:16}}>Get AI daily kitchen briefing</div>
              <button onClick={()=>runAnalysis('briefing')} style={{padding:'10px 24px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Generate Daily Brief
              </button>
            </div>
          ) : (
            <div>
              {/* Status */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 2fr',gap:14,marginBottom:16}}>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,textAlign:'center'}}>
                  <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginBottom:10}}>Kitchen Status</div>
                  <div style={{fontSize:24,fontWeight:900,marginBottom:6,
                    color:briefing.kitchen_status==='Ready'?C.accent:briefing.kitchen_status==='Critical'?C.red:C.amber}}>
                    {briefing.kitchen_status==='Ready'?'✅':briefing.kitchen_status==='Critical'?'🚨':'⚠️'}
                  </div>
                  <div style={{fontSize:16,fontWeight:800,color:briefing.kitchen_status==='Ready'?C.accent:briefing.kitchen_status==='Critical'?C.red:C.amber}}>
                    {briefing.kitchen_status}
                  </div>
                </div>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.gold,marginBottom:8}}>Today's Summary</div>
                  <div style={{fontSize:14,color:C.text2,lineHeight:1.7}}>{briefing.today_summary}</div>
                </div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                {/* Priorities */}
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.red,marginBottom:12}}>🎯 Top Priorities</div>
                  {(briefing.top_priorities||[]).map((p,i)=>(
                    <div key={i} style={{display:'flex',gap:10,padding:'8px 12px',background:C.surface2,borderRadius:8,marginBottom:6}}>
                      <span style={{color:C.red,fontWeight:800}}>{i+1}.</span>
                      <span style={{fontSize:13,color:C.text2}}>{p}</span>
                    </div>
                  ))}
                </div>

                {/* Purchase urgency */}
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.amber,marginBottom:12}}>
                    🛒 Order Today
                    <span style={{marginLeft:8,padding:'2px 8px',borderRadius:99,fontSize:11,background:C.amberBg,color:C.amber}}>{briefing.purchase_urgency} urgency</span>
                  </div>
                  {(briefing.items_to_order_today||[]).map((item,i)=>(
                    <div key={i} style={{display:'flex',gap:8,padding:'7px 12px',background:C.surface2,borderRadius:8,marginBottom:6}}>
                      <span style={{color:C.amber}}>•</span>
                      <span style={{fontSize:13,color:C.text2}}>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Prep checklist */}
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.accent,marginBottom:12}}>✅ Prep Checklist</div>
                  {(briefing.prep_checklist||[]).map((task,i)=>(
                    <label key={i} style={{display:'flex',gap:10,padding:'8px 12px',background:C.surface2,borderRadius:8,marginBottom:6,cursor:'pointer',alignItems:'center'}}>
                      <input type="checkbox" style={{cursor:'pointer'}} />
                      <span style={{fontSize:13,color:C.text2}}>{task}</span>
                    </label>
                  ))}
                </div>

                {/* Food safety */}
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.blue,marginBottom:12}}>🛡️ Food Safety Reminders</div>
                  {(briefing.food_safety_reminders||[]).map((r,i)=>(
                    <div key={i} style={{display:'flex',gap:8,padding:'7px 12px',background:C.surface2,borderRadius:8,marginBottom:6}}>
                      <span style={{color:C.blue}}>•</span>
                      <span style={{fontSize:13,color:C.text2}}>{r}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DEMAND FORECAST */}
      {activeTab==='demand' && (
        <div>
          {!demandForecast ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>📊</div>
              <div style={{fontSize:14,color:C.text2,marginBottom:16}}>AI demand forecast based on upcoming bookings</div>
              <button onClick={()=>runAnalysis('demand')} style={{padding:'10px 24px',background:C.amber,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Generate Demand Forecast
              </button>
            </div>
          ) : (
            <div>
              <div style={{fontSize:13,color:C.text2,marginBottom:16,padding:'12px 14px',background:C.surface,borderRadius:10,border:`1px solid ${C.border}`}}>
                🧠 {demandForecast.summary}
              </div>

              {/* Daily forecast */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:14,fontWeight:800,color:C.amber,marginBottom:12}}>📅 7-Day Meal Forecast</div>
                <div style={{display:'grid',gap:10}}>
                  {(demandForecast.daily_forecast||[]).map((day,i)=>(
                    <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,borderLeft:`4px solid ${day.prep_priority==='High'?C.red:day.prep_priority==='Medium'?C.amber:C.accent}`}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                        <div>
                          <div style={{fontSize:14,fontWeight:800,color:C.text}}>{day.day} · {new Date(day.date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</div>
                          <div style={{fontSize:11,color:C.gold,marginTop:2}}>{day.special_notes}</div>
                        </div>
                        <span style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:700,
                          background:day.prep_priority==='High'?C.redBg:day.prep_priority==='Medium'?C.amberBg:C.accentBg,
                          color:day.prep_priority==='High'?C.red:day.prep_priority==='Medium'?C.amber:C.accent}}>
                          {day.prep_priority} Priority
                        </span>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:10}}>
                        {[['🌅 Breakfast',day.breakfast_covers],['☀️ Lunch',day.lunch_covers],['🌙 Dinner',day.dinner_covers]].map(([label,count])=>(
                          <div key={label} style={{textAlign:'center',padding:'8px',background:C.surface2,borderRadius:8}}>
                            <div style={{fontSize:20,fontWeight:900,color:C.amber}}>{count}</div>
                            <div style={{fontSize:10,color:C.text3}}>{label}</div>
                          </div>
                        ))}
                      </div>
                      {day.key_dishes_to_prep?.length>0 && (
                        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                          <span style={{fontSize:10,color:C.text3}}>Key prep:</span>
                          {day.key_dishes_to_prep.map((dish,j)=>(
                            <span key={j} style={{padding:'2px 8px',borderRadius:99,fontSize:10,background:C.surface2,color:C.text2,border:`1px solid ${C.border}`}}>{dish}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Purchase list */}
              {demandForecast.purchase_list?.length>0 && (
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.red,marginBottom:12}}>🛒 AI Purchase List</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:8}}>
                    {demandForecast.purchase_list.map((item,i)=>{
                      const u = URGENCY[item.urgency]||URGENCY.Medium;
                      return (
                        <div key={i} style={{padding:'10px 14px',background:C.surface2,borderRadius:8,border:`1px solid ${u.color}33`}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                            <div style={{fontSize:13,fontWeight:700,color:C.text}}>{item.item}</div>
                            <span style={{fontSize:11,fontWeight:700,color:u.color}}>{u.icon} {item.urgency}</span>
                          </div>
                          <div style={{fontSize:12,color:C.amber,fontWeight:600}}>{item.quantity}</div>
                          <div style={{fontSize:11,color:C.text3,marginTop:2}}>{item.reason}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Staffing */}
              {demandForecast.staffing_recommendation && (
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.blue,marginBottom:12}}>👨‍🍳 Staffing Recommendation</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                    {[
                      ['Chefs',demandForecast.staffing_recommendation.chefs_needed],
                      ['Kitchen Helpers',demandForecast.staffing_recommendation.kitchen_helpers],
                      ['Service Staff',demandForecast.staffing_recommendation.service_staff],
                    ].map(([label,count])=>(
                      <div key={label} style={{textAlign:'center',padding:'14px',background:C.surface2,borderRadius:8}}>
                        <div style={{fontSize:24,fontWeight:900,color:C.blue}}>{count}</div>
                        <div style={{fontSize:11,color:C.text3,marginTop:4}}>{label}</div>
                      </div>
                    ))}
                    <div style={{textAlign:'center',padding:'14px',background:C.surface2,borderRadius:8}}>
                      <div style={{fontSize:12,fontWeight:700,color:C.amber}}>Peak Day</div>
                      <div style={{fontSize:12,color:C.text2,marginTop:4}}>{demandForecast.staffing_recommendation.peak_day?new Date(demandForecast.staffing_recommendation.peak_day).toLocaleDateString('en-IN',{day:'numeric',month:'short'}):'-'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Food cost estimate */}
              {demandForecast.food_cost_estimate && (
                <div style={{background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:12,padding:18}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:12}}>💰 Food Cost Estimate</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                    <div style={{textAlign:'center',padding:'16px',background:C.surface2,borderRadius:8}}>
                      <div style={{fontSize:24,fontWeight:900,color:C.gold}}>{inr(demandForecast.food_cost_estimate.this_week)}</div>
                      <div style={{fontSize:11,color:C.text3,marginTop:4}}>This Week Food Cost</div>
                    </div>
                    <div style={{textAlign:'center',padding:'16px',background:C.surface2,borderRadius:8}}>
                      <div style={{fontSize:24,fontWeight:900,color:C.amber}}>{inr(demandForecast.food_cost_estimate.per_cover_avg)}</div>
                      <div style={{fontSize:11,color:C.text3,marginTop:4}}>Per Cover Average</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* INVENTORY ALERTS */}
      {activeTab==='stock' && (
        <div>
          <div style={{fontSize:14,fontWeight:800,color:C.red,marginBottom:14}}>
            ⚠️ Low Stock Items ({(context?.low_stock||[]).length})
          </div>
          {(context?.low_stock||[]).length===0 ? (
            <div style={{textAlign:'center',padding:40,background:C.surface,borderRadius:12,color:C.accent,border:`1px solid ${C.accent}33`}}>
              ✅ All stock levels are healthy!
            </div>
          ) : (
            <div style={{display:'grid',gap:8}}>
              {(context?.low_stock||[]).map((item,i)=>(
                <div key={i} style={{background:C.surface,border:`1px solid ${item.shortage_pct>70?C.red:item.shortage_pct>40?C.amber:C.border}33`,borderRadius:10,padding:'14px 18px',display:'flex',justifyContent:'space-between',alignItems:'center',borderLeft:`4px solid ${item.shortage_pct>70?C.red:item.shortage_pct>40?C.amber:C.gold}`}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:C.text}}>{item.item_name}</div>
                    <div style={{fontSize:12,color:C.text3,marginTop:2}}>{item.category} · Vendor: {item.vendor_name||'Not set'}</div>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,textAlign:'center'}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:800,color:C.red}}>{item.current_stock} {item.unit}</div>
                      <div style={{fontSize:9,color:C.text3}}>Current</div>
                    </div>
                    <div>
                      <div style={{fontSize:14,fontWeight:800,color:C.amber}}>{item.reorder_level} {item.unit}</div>
                      <div style={{fontSize:9,color:C.text3}}>Reorder At</div>
                    </div>
                    <div>
                      <div style={{fontSize:14,fontWeight:800,color:item.shortage_pct>70?C.red:C.amber}}>{item.shortage_pct}%</div>
                      <div style={{fontSize:9,color:C.text3}}>Short</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MENU ENGINEERING */}
      {activeTab==='menu' && (
        <div>
          {!menuAnalysis ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>⭐</div>
              <div style={{fontSize:14,color:C.text2,marginBottom:8}}>AI Menu Engineering Analysis</div>
              <div style={{fontSize:12,color:C.text3,marginBottom:16}}>Classify menu items as Star ⭐ Plowhorse 🐎 Puzzle 🧩 Dog 🐕</div>
              <button onClick={()=>runAnalysis('menu_engineering')} style={{padding:'10px 24px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Run Menu Engineering
              </button>
            </div>
          ) : (
            <div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 2fr',gap:14,marginBottom:16}}>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,textAlign:'center'}}>
                  <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginBottom:8}}>Menu Health Score</div>
                  <div style={{fontSize:52,fontWeight:900,color:menuAnalysis.menu_health_score>=70?C.accent:menuAnalysis.menu_health_score>=50?C.amber:C.red}}>
                    {menuAnalysis.menu_health_score}
                  </div>
                </div>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.gold,marginBottom:8}}>AI Assessment</div>
                  <div style={{fontSize:13,color:C.text2,lineHeight:1.7}}>{menuAnalysis.summary}</div>
                </div>
              </div>

              {/* Category analysis */}
              {menuAnalysis.category_analysis?.length>0 && (
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:12}}>📊 Category Classification</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:8}}>
                    {menuAnalysis.category_analysis.map((cat,i)=>{
                      const cls = MENU_CLASS[cat.classification]||MENU_CLASS.Puzzle;
                      return (
                        <div key={i} style={{padding:'12px 14px',background:C.surface2,borderRadius:10,border:`1px solid ${cls.color}33`}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                            <div style={{fontSize:13,fontWeight:700,color:C.text}}>{cat.category}</div>
                            <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:cls.bg,color:cls.color}}>{cls.icon} {cat.classification}</span>
                          </div>
                          <div style={{fontSize:11,color:C.text3,marginBottom:4}}>{cls.desc}</div>
                          <div style={{fontSize:12,color:C.gold}}>{cat.recommendation}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Event menus */}
              {menuAnalysis.event_menu_suggestions && (
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:12}}>🎯 Recommended Menus by Event Type</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
                    {Object.entries(menuAnalysis.event_menu_suggestions).map(([event,items])=>(
                      <div key={event} style={{padding:'14px',background:C.surface2,borderRadius:10}}>
                        <div style={{fontSize:13,fontWeight:800,color:C.gold,marginBottom:8,textTransform:'capitalize'}}>💍 {event}</div>
                        {(items||[]).map((item,j)=>(
                          <div key={j} style={{fontSize:12,color:C.text2,padding:'4px 0',borderBottom:`1px solid ${C.border}`}}>• {item}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CHAT */}
      {activeTab==='chat' && (
        <div>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,marginBottom:12,overflow:'hidden'}}>
            <div style={{maxHeight:420,overflowY:'auto',padding:20}}>
              {chatMessages.length===0 && (
                <div style={{textAlign:'center',padding:40,color:C.text3}}>
                  <div style={{fontSize:32,marginBottom:10}}>🍽️</div>
                  <div style={{fontSize:14,color:C.text2,marginBottom:16}}>Ask Kitchen AI anything</div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center'}}>
                    {['How many kg of paneer for 200 pax dinner?','What dishes to avoid in summer?','How to reduce food cost by 10%?','Best buffet menu for a corporate group?'].map(q=>(
                      <button key={q} onClick={()=>setChatInput(q)} style={{padding:'6px 12px',background:C.surface2,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:11,cursor:'pointer'}}>{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((m,i)=>(
                <div key={i} style={{marginBottom:14,display:'flex',flexDirection:m.role==='user'?'row-reverse':'row',gap:8}}>
                  <div style={{width:28,height:28,borderRadius:'50%',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',
                    background:m.role==='user'?'#153D4B':`linear-gradient(135deg,${C.gold},#E8C97A)`}}>
                    {m.role==='user'?'👤':'🍽️'}
                  </div>
                  <div style={{maxWidth:'80%',padding:'10px 14px',borderRadius:10,background:m.role==='user'?'#153D4B':C.surface2,border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:13,lineHeight:1.7,color:C.text,whiteSpace:'pre-wrap'}}>{m.content}</div>
                  </div>
                </div>
              ))}
              {chatLoading && <div style={{textAlign:'center',padding:10,color:C.text3}}>⏳ Kitchen AI thinking...</div>}
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <input value={chatInput} onChange={e=>setChatInput(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&askKitchen()}
              placeholder="Ask about recipes, quantities, food cost, menu planning..."
              style={{flex:1,padding:'12px 16px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:10,color:C.text,fontSize:13,outline:'none'}} />
            <button onClick={askKitchen} disabled={chatLoading||!chatInput.trim()}
              style={{padding:'12px 20px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',opacity:chatLoading||!chatInput.trim()?.5:1}}>
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
